<template>
  <div class="uk-container">
    <template v-if="!enquiry">
      <div class="uk-alert-danger uk-width-medium uk-margin-auto uk-text-center" uk-alert>
        <p>Oops! <br> Something went wrong.</p>
      </div>
    </template>

    <template v-else>
      <div class="uk-card uk-card-default uk-width-large uk-margin-auto">
        <div class="uk-card-header uk-text-center">
          <h3 class="uk-card-title">Enquiry</h3>
          <p class="uk-text-meta">{{ enquiry.id }}</p>
        </div>

        <div class="uk-card-body uk-text-center uk-padding-remove card-content">
          <div class="section">
            <div class="section-title">Details</div>
            <div class="section-content">
              <div class="item">
                <div class="item-title">Name</div>
                <div class="item-content">
                  <div class="composite">
                    <div class="composite-part">
                      <div class="value">{{ enquiry.content.name.first || 'N/A' }}</div>
                      <div class="label">First</div>
                    </div>
                    <div class="composite-part">
                      <div class="value">{{ enquiry.content.name.last || 'N/A' }}</div>
                      <div class="label">Last</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Phone</div>
                <div class="item-content">
                  {{ enquiry.content.contact.phone || 'N/A' }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Email</div>
                <div class="item-content">
                  {{ enquiry.content.contact.email || 'N/A' }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Contact Notes</div>
                <div class="item-content">
                  {{ enquiry.content.contact.notes || 'N/A' }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Area</div>
                <div class="item-content">
                  {{ (enquiry.content.location.area) ? $helpers.getAreaName(enquiry.content.location.area) : 'N/A' }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Customer Location</div>
                <div class="item-content">
                  {{ enquiry.content.location.customer || 'N/A' }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Source</div>
                <div class="item-content">
                  {{ source }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Details</div>
                <div class="item-content">
                  {{ enquiry.content.details }}
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Info</div>
            <div class="section-content">
              <div class="item">
                <div class="item-title">Operator</div>
                <div class="item-content">
                  <template v-if="operator === 'web'">
                    Direct - Website Form
                  </template>
                  <template v-else>
                    {{ operator.name.first }} {{ operator.name.last }}
                  </template>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Created</div>
                <div class="item-content">
                  {{ $moment(enquiry.info.created.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss') }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Updated</div>
                <div class="item-content">
                  {{ enquiry.info.updated && enquiry.info.updated.timestamp ? $moment(enquiry.info.updated.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss') : 'Never' }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Status</div>
                <div class="item-content" v-html="formatStatus(enquiry.info.status)" />
              </div>
              <div v-if="enquiry.info.appointment" class="item">
                <div class="item-title">Appointment</div>
                <div class="item-content">
                  {{ enquiry.info.appointment }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="$store.getters['account/type'] === 'operator' || enquiry.info.appointment" class="uk-card-footer">
          <div v-if="enquiry.info.appointment" class="uk-text-center">
            <router-link :to="{ name: 'appointment-single', params: { id: enquiry.info.appointment }}" class="uk-button uk-button-primary">View Appointment</router-link>
          </div>

          <div v-else class="uk-flex uk-flex-between">
            <button class="uk-button uk-button-secondary">Update Status</button>
            <div id="status-drop" uk-drop="mode: click; pos: top-center; delay-hide: 0;" style="width: 160px;">
              <div class="options">
                <template v-for="status in statusOptions">
                  <button v-if="status.id !== enquiry.info.status" :key="status.id" class="uk-button" :class="`uk-button-${status.class}`" @click="updateStatus(status.id)">{{ status.value }}</button>
                </template>
              </div>
            </div>
            <button class="uk-button uk-button-primary" :disabled="conversionDisabled" @click="createAppointment">Create Appointment</button>
          </div>
        </div>
      </div>

      <div class="uk-margin-top history">
        <div class="uk-card uk-card-default uk-width-large uk-margin-auto">
          <div class="uk-card-header uk-text-center">
            <h3 class="uk-card-title">History</h3>
            <p class="uk-text-meta">Logs - User Actions</p>
          </div>

          <div class="uk-card-body uk-padding-remove uk-text-center actions">
            <div v-for="(action, index) in actions" :key="index" class="action">
              <div class="description" :class="{ hover: action.details }">
                <div class="message">
                  {{ action.message.primary }}

                  <template v-if="action.message.secondary">
                    <span v-html="action.message.secondary" />
                  </template>
                </div>

                <div class="info uk-text-meta">
                  <span :uk-tooltip="`title: ${action.info.timestamp.default}; pos: bottom;`">{{ action.info.timestamp.human }}</span> - <b>{{ action.info.user }}</b>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
export default {
  name: 'ViewEnquirySingle',

  computed: {
    enquiry() {
      return this.$store.getters['enquiries/find'](this.$route.params.id)
    },

    operator() {
      let response = null

      if (this.enquiry.info.created.user === 'web') {
        response = 'web'
      } else {
        response = this.$store.getters['users/find'](this.enquiry.info.created.user)
      }

      return response
    },

    source() {
      return this.enquiry.content.source ? this.$helpers.channels.find((channel) => channel.id === this.enquiry.content.source).value : 'N/A'
    },

    conversionDisabled() {
      return !((this.enquiry.info.status === 'new' || this.enquiry.info.status === 'open'))
    },

    statusOptions() {
      return this.$helpers.status.enquiry.filter((item) => item.id !== 'new')
    },

    actions() {
      const actions = []
      // Slice is used to make a shallow copy of an array to prevent state mutation and infinite loop.
      const log = this.enquiry.log.slice().reverse()

      log.forEach((action) => {
        const { type } = action
        const user = this.getUser(action.user)
        const message = {}
        const info = {
          timestamp: {
            human: this.$moment(action.timestamp.seconds, 'X').fromNow(),
            default: this.$moment(action.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss')
          },
          user
        }

        if (type === 'create') {
          message.primary = 'Enquiry was created.'
        }

        if (type === 'update') {
          if (action.subtype && action.subtype === 'status') {
            const status = this.$helpers.status.enquiry.find((option) => option.id === action.content)

            message.primary = 'Enquiry status was updated to'
            message.secondary = `<b>${status.value}</b>.`
          }

          if (action.subtype && action.subtype === 'convert') {
            message.primary = 'Enquiry was converted to Appointment.'
          }
        }

        actions.push({ info, message })
      })

      return actions
    }
  },

  methods: {
    getUser(id) {
      const user = this.$store.getters['users/find'](id)
      return (user) ? `${user.name.first} ${user.name.last}` : 'Web'
    },

    formatStatus(data) {
      let label = '<div class="uk-label uk-label-danger">N/A</div>'
      const option = this.$helpers.status.enquiry.find((o) => o.id === data)
      label = `<div class="uk-label uk-label-${option.class}">${option.value}</div>`
      return label
    },

    async updateStatus(status) {
      this.$uikit.drop('#status-drop').hide()
      const loader = this.$loader.show()
      const user = this.$store.state.account.record.uid

      const data = {
        'info.status': status,
        'info.updated.timestamp': this.$firebase.firestore.FieldValue.serverTimestamp(),
        'info.updated.user': user,
        log: this.$firebase.firestore.FieldValue.arrayUnion({
          timestamp: this.$firebase.firestore.Timestamp.now(),
          user,
          type: 'update',
          subtype: 'status',
          content: status
        })
      }

      try {
        await this.$firebase.firestore().collection(this.$globals.firebase.collection.enquiries).doc(this.enquiry.id).update(data)
        this.$uikit.notification('The Enquiry was updated successfully!', 'success')
      } catch (error) {
        console.log(error)
        this.$uikit.notification('Something went wrong while updating the enquiry. Please try again later, or contact support.', 'danger')
      } finally {
        loader.hide()
      }
    },

    createAppointment() {
      this.$router.push({ name: 'appointment-create', query: { e: this.enquiry.id } })
    }
  }
}
</script>

<style lang="scss" scoped>
.options {
  display: flex;
  flex-direction: column;
}
</style>
